import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { collectionAPI } from '../../api/waybillAPI';
import { useAuth } from '../../context/AuthContext';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
};

const methodLabels = {
  cash: 'Cash', check: 'Check', bank_transfer: 'Bank Transfer',
  gcash: 'GCash', other: 'Other',
};

const deductionLabels = {
  discount: 'Discount', damage: 'Damage', missing: 'Missing', general_expense: 'General Expense', '2307': '2307', other: 'Other',
};

export default function DailyCollectionReportPage() {
  const today = new Date().toISOString().split('T')[0];
  const printRef = useRef();
  const { selectedClient } = useAuth();
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;

  const [date, setDate]       = useState(today);
  const [billType, setBillType] = useState('');
  const [collections, setCollections] = useState([]);
  const [summary, setSummary] = useState({ total: 0, count: 0, byMethod: {} });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  const loadReport = async (d = date, bt = billType) => {
    setLoading(true);
    try {
      const params = { date: d, billType: bt };
      if (accountingClientId) params.accountingClient = accountingClientId;
      const res = await collectionAPI.getDailyCollection(params);
      const data = res.data || {};
      setCollections(Array.isArray(data.data) ? data.data : []);
      setSummary(data.summary || { total: 0, count: 0, byMethod: {} });
      setLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [accountingClientId]); // eslint-disable-line

  const handlePrint = () => window.print();

  const fmtDateLong = (ds) =>
    new Date(ds + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <Layout pageTitle="Daily Collection Report">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-header { display: block !important; }
          @page { size: A4 landscape; margin: 10mm; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #999; padding: 3px 5px; font-size: 8.5pt; }
          body { font-size: 9pt; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Print-only header */}
      <div className="print-header" style={{ textAlign: 'center', marginBottom: 8 }}>
        <h1 style={{ fontSize: '14pt', margin: 0 }}>DAILY COLLECTION REPORT</h1>
        <p style={{ margin: '2px 0', fontSize: '10pt' }}>Date: {loaded ? fmtDateLong(date) : date}</p>
        <p style={{ margin: '2px 0', fontSize: '10pt' }}>Total Collected: ₱{PHP(summary.total)}</p>
      </div>

      <div className="space-y-4 max-w-7xl" ref={printRef}>
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg shadow px-4 py-3 no-print">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Bill Type</label>
            <select value={billType} onChange={(e) => setBillType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              <option value="1">FF Billing</option>
              <option value="2">Trucking Billing</option>
            </select>
          </div>
          <button onClick={() => loadReport()} disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Loading…' : 'Load'}
          </button>
          {loaded && collections.length > 0 && (
            <button onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
              Print
            </button>
          )}
        </div>

        {/* Summary cards */}
        {loaded && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-400 uppercase mb-1">Total Collected</p>
              <p className="text-2xl font-bold text-teal-600">₱{PHP(summary.total)}</p>
              <p className="text-xs text-gray-400 mt-1">{summary.count} record(s)</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-400 uppercase mb-1">By Payment Method</p>
              <div className="space-y-1 mt-1">
                {Object.entries(summary.byMethod || {}).map(([method, amt]) => (
                  <div key={method} className="flex justify-between text-sm">
                    <span className="text-gray-600">{methodLabels[method] || method}</span>
                    <span className="font-medium text-teal-700">₱{PHP(amt)}</span>
                  </div>
                ))}
                {Object.keys(summary.byMethod || {}).length === 0 && (
                  <p className="text-xs text-gray-400">—</p>
                )}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-400 uppercase mb-2">Deductions Applied</p>
              <div className="space-y-1">
                {Object.entries(summary.byDeductionType || {}).map(([type, amt]) => (
                  <div key={type} className="flex justify-between text-sm">
                    <span className="text-gray-600">{deductionLabels[type] || type}</span>
                    <span className="font-medium text-orange-600">₱{PHP(amt)}</span>
                  </div>
                ))}
                {Object.keys(summary.byDeductionType || {}).length === 0 && (
                  <p className="text-xs text-gray-400">No deductions</p>
                )}
                {summary.totalDeductions > 0 && (
                  <div className="flex justify-between text-sm border-t border-gray-100 pt-1 mt-1">
                    <span className="text-gray-600 font-medium">Total</span>
                    <span className="font-semibold text-orange-600">₱{PHP(summary.totalDeductions)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loaded && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Bill No.', 'Waybill Serial', 'Customer', 'Bill Date', 'Due Date',
                    'Payment Date', 'Amount', 'Method', 'Reference No.', 'Notes', 'Received By'].map((h) => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {collections.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400">No collections found.</td></tr>
                ) : (
                  collections.map((c, i) => {
                    const bill        = c.billId || {};
                    const waybill     = bill.WayBillId || {};
                    const customer    = waybill.CustomerId || {};
                    const custName    = customer.CustomerName || customer.name || '—';
                    const waybillSerial = waybill.SerialNo || '—';
                    return (
                      <tr key={c._id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-teal-700">{bill.BillName || '—'}</td>
                        <td className="px-3 py-2">{waybillSerial}</td>
                        <td className="px-3 py-2">{custName}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(bill.BillDate)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(bill.BillDueDate)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(c.paymentDate)}</td>
                        <td className={`px-3 py-2 font-medium whitespace-nowrap ${c.transactionType === 'deduction' ? 'text-orange-600' : 'text-green-700'}`}>₱{PHP(c.amount)}</td>
                        <td className="px-3 py-2">
                          {c.transactionType === 'deduction'
                            ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{deductionLabels[c.deductionType] || c.deductionType}</span>
                            : <span className="capitalize">{methodLabels[c.paymentMethod] || c.paymentMethod}</span>
                          }
                        </td>
                        <td className="px-3 py-2 text-gray-500">{c.referenceNo || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{c.notes || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{c.receivedByName || '—'}</td>
                      </tr>
                    );
                  })
                )}
                {collections.length > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={7} className="px-3 py-2 text-right text-gray-600">Total</td>
                    <td className="px-3 py-2 text-green-700 whitespace-nowrap">₱{PHP(summary.total)}</td>
                    <td colSpan={4} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
